import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getApiBase } from "../utils/apiBase";

/**
 * Zoho OAuth redirect handler. Registered redirect URI must match Zoho API Console
 * and server env ZOHO_OAUTH_REDIRECT_URI (e.g. https://app.kuzkohistory.com/zoho-oauthgrant).
 */
function ZohoOAuthGrant() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const oauthError = searchParams.get("error");
  const errorDescription =
    searchParams.get("error_description") || searchParams.get("error_reason");
  const accountsServer = searchParams.get("accounts-server");

  const [status, setStatus] = useState(
    code ? "exchanging" : oauthError ? "oauth_error" : "missing",
  );
  const [result, setResult] = useState(null);
  const [exchangeError, setExchangeError] = useState(null);
  const didExchange = useRef(false);

  useEffect(() => {
    if (!code || didExchange.current) return;
    didExchange.current = true;

    const url = `${getApiBase()}/api/zoho/oauth/exchange`;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, accountsServer }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error(
              "Could not reach the API server (HTTP 404). The Zoho callback must POST to your Node " +
                "server. If the app is on app.kuzkohistory.com, set REACT_APP_API_URL to your API " +
                "origin when building (no trailing slash), e.g. REACT_APP_API_URL=https://api.kuzkohistory.com, " +
                "then rebuild and redeploy. Or configure your host to proxy /api to the Node process.",
            );
          }
          throw new Error(data.error || data.message || `HTTP ${res.status}`);
        }
        return data;
      })
      .then((data) => {
        setResult(data);
        setStatus("success");
      })
      .catch((err) => {
        setExchangeError(err.message || "Token exchange failed");
        setStatus("exchange_failed");
      });
  }, [code, accountsServer]);

  if (oauthError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-lg rounded-lg border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-red-700">
            Zoho authorization was denied
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {errorDescription || oauthError}
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm font-medium text-primary-600 hover:underline"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!code) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-800">
            Zoho OAuth callback
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            No authorization code was returned. Start the flow from the Zoho
            authorize URL (with{" "}
            <code className="rounded bg-slate-100 px-1">access_type=offline</code>
            ).
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm font-medium text-primary-600 hover:underline"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (status === "exchanging") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-slate-700">Exchanging authorization code with Zoho…</p>
        </div>
      </div>
    );
  }

  if (status === "exchange_failed") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-lg rounded-lg border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-red-700">
            Token exchange failed
          </h1>
          <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">
            {exchangeError}
          </p>
          <p className="mt-4 text-xs text-slate-500">
            Ensure the server has{" "}
            <code className="rounded bg-slate-100 px-1">ZOHO_CLIENT_ID</code>,{" "}
            <code className="rounded bg-slate-100 px-1">ZOHO_CLIENT_SECRET</code>
            , and{" "}
            <code className="rounded bg-slate-100 px-1">
              ZOHO_OAUTH_REDIRECT_URI
            </code>{" "}
            set, and that the redirect URI matches this page&apos;s URL exactly.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm font-medium text-primary-600 hover:underline"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-2xl rounded-lg border border-emerald-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold text-emerald-800">
          Zoho connected successfully
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Copy the refresh token and API domain into your server environment
          (e.g. <code className="rounded bg-slate-100 px-1">ZOHO_REFRESH_TOKEN</code>
          , <code className="rounded bg-slate-100 px-1">ZOHO_API_DOMAIN</code>).
          Do not commit secrets to git or share them in chat.
        </p>

        {result?.api_domain && (
          <div className="mt-6">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              API domain
            </p>
            <pre className="mt-1 overflow-x-auto rounded border border-slate-200 bg-slate-50 p-3 text-sm">
              {result.api_domain}
            </pre>
          </div>
        )}

        {result?.refresh_token && (
          <div className="mt-6">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Refresh token
            </p>
            <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-slate-50 p-3 text-sm">
              {result.refresh_token}
            </pre>
          </div>
        )}

        {result?.expires_in != null && (
          <p className="mt-4 text-xs text-slate-500">
            Access token expires in {result.expires_in} seconds (the refresh token
            is used to obtain new access tokens).
          </p>
        )}

        <Link
          to="/"
          className="mt-8 inline-block text-sm font-medium text-primary-600 hover:underline"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}

export default ZohoOAuthGrant;
